import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import FacilityMap from './FacilityMap'
import { CDropdown, CDropdownToggle, CDropdownMenu, CDropdownItem, CModalFooter, } from '@coreui/react'
import FacilityCRUD from '../../services/general/getFacilityData.api.js'
import FacilityTypeFn from '../../services/general/modifyFacilityType.api'
import AutoComplete from './AutoComplete'
import FacilityForm from './FacilityForm'
import { Form } from '@formio/react'
import fetchPatientData from '../../services/general/patientGeneralAPI.api.js'
import SavePatients from '../../services/general/savePatientinformation.api.js'
import GetDiagnosisData from '../../services/general/getDiagnosisData.api'
import MedicineCRUD from '../../services/general/Medication.api'
import PatientMedications from '../../services/general/patientMedicationData.api'
import PatientNoteDataCRUD from '../../services/general/patientNoteData.api'
import { Download, Copy } from 'lucide-react'
import { FiFilter } from 'react-icons/fi'
import Select from 'react-select'
import toast from 'react-hot-toast'
import { toastConfig } from '../../constants/other.config'
import { CButton, CNav, CTabContent, CTabPane, CNavItem, CNavLink, CRow, CCol, CModal, CModalBody, CForm, CModalHeader, CModalTitle, CFormLabel, CFormInput, CFormTextarea } from '@coreui/react'
import providerCredentialingCRUD from '../../services/general/ProviderCredentialingCRUD.api.js'
import { buildCredentialingTemplate } from './kanban-components/credentialingutility'
import AppSelect from '../../components/shared/AppSelect'
import CustomDatePickingFormat from '../../components/shared/CustomDatePickingFormat'

const parseDashboardMapPayload = (response, fallback = []) => {
  try {
    const payload = typeof response?.d === 'string' ? JSON.parse(response.d) : response?.d || response
    return payload?.Data || payload?.data || payload || fallback
  } catch (error) {
    console.error('Unable to parse dashboard map payload:', error)
    return fallback
  }
}

const normalizeDashboardMapText = (value) => String(value || '').trim().toLowerCase()

const normalizeDashboardMapId = (value) => String(value || '').trim().toLowerCase()

const normalizeDashboardMapCompare = (value) =>
  normalizeDashboardMapText(value)
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const parseDashboardMapJsonValue = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (error) {
    return value
  }
}

const unwrapDashboardMapPatient = (patient) => {
  if (!patient || typeof patient !== 'object') return patient

  const jsonData = parseDashboardMapJsonValue(patient.JsonData || patient.jsonData)
  const data = parseDashboardMapJsonValue(patient.Data || patient.data)
  const facility = parseDashboardMapJsonValue(patient.Facility || patient.facility)

  return {
    ...patient,
    ...(jsonData && typeof jsonData === 'object' && !Array.isArray(jsonData) ? jsonData : {}),
    ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}),
    ...(facility && typeof facility === 'object' && !Array.isArray(facility)
      ? {
          FacilityID: patient.FacilityID || patient.facilityID || facility.FacilityID || facility.facilityID || facility.value || facility.ID || facility.id,
          FacilityName: patient.FacilityName || patient.facilityName || facility.FacilityName || facility.facilityName || facility.label || facility.Name || facility.name,
        }
      : {}),
  }
}

const getDashboardMapPatientId = (patient) =>
  normalizeDashboardMapId(patient?.PatientID || patient?.ID || patient?.patientID || patient?.id)

const getDashboardMapFacilityId = (facility) =>
  normalizeDashboardMapId(facility?.ID || facility?.FacilityID || facility?.facilityID || facility?.id)

const getDashboardMapMedicationNames = (entry) => {
  if (!entry) return []
  if (typeof entry !== 'object') return [String(entry)]

  const nestedData = entry?.Data || entry?.data
  if (typeof nestedData === 'string') {
    try {
      return getDashboardMapMedicationNames(JSON.parse(nestedData))
    } catch (error) {
      return []
    }
  }
  if (Array.isArray(nestedData)) return nestedData.flatMap(getDashboardMapMedicationNames)
  if (nestedData && typeof nestedData === 'object') return getDashboardMapMedicationNames(nestedData)

  return [
    entry?.MedicineName,
    entry?.Name,
    entry?.name,
    entry?.label,
    entry?.medicationName,
    entry?.MedicationName,
    entry?.OtherMedicineName,
    entry?.otherName,
    entry?.OtherName,
    entry?.othername,
  ].filter(Boolean)
}

const AddFacility = ({ dashboardMapView = false }) => {
  const newFacility = useRef([])
  const [searchValue, setSearchValue] = useState('')
  const [visible, setVisible] = useState(false)
  const [schema, setSchema] = useState({ components: [] })
  const [formAnswer, setFormAnswer] = useState({ components: [] })
  const [facilities, setFacilities] = useState([])
  const allFacilities = useRef([])
  const [facilityType, setFacilityType] = useState([])
  const [providerList, setProviderList] = useState([])
  const [modalVisible, setModalVisible] = useState(false)
  const rowData = useRef({})
  const [selectedProvider, setSelectedProvider] = useState([])
  const [selectedProviderID, setSelectedProviderID] = useState('')
  const [selectedProviderName, setSelectedProviderName] = useState('')
  const [radius, setRadius] = useState(6)
  const [selectedProviderFacilities, setSelectedProviderFacilities] = useState([])
  const [options, setOptions] = useState([])
  const [selectedCounty, setSelectedCounty] = useState('')
  const [countyList, setCountyList] = useState([])
  const [selectedFacility, setSelectedFacilities] = useState([])
  const [facilityCounter, setFacilityCounter] = useState(0)
  const [latlang, setLatlang] = useState(null)
  const [typeName, setTypeName] = useState(null)
  const [selectedEditFacility, setSelectedEditFacility] = useState(null)
  const [isToggled, setIsToggled] = useState(false)
  const [providerSelectedFacility, setProviderSelectedFacility] = useState([])
  const [updateFacility, setUpdateFacility] = useState({})
  const [providerFullDetails, setProviderFullDetails] = useState(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const [scheduleAppointmentPatinet, setScheduledPatient] = useState([])
  const [selectedCredentialed, setSelectedCredentialed] = useState('Yes')
  const [previousCredentialedStatus, setPreviousCredentialedStatus] = useState(null)
  const [selectedDays, setSelectedDays] = useState('')
  const [refreshProviderFacilities, setRefreshProviderFacilities] = useState(false)
  const [activeTab, setActiveTab] = useState('changeFacilityDetails')
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [activeStatusFilter, setActiveStatusFilter] = useState(null)
  const [searchTerm, setSearchTerm] = useState("");
  const [showCredentialingModal, setShowCredentialingModal] = useState(false)
  const [dashboardFilterVisible, setDashboardFilterVisible] = useState(false)
  const DEFAULT_FILTERS = {
    diagnosis: '',
    medication: '',
    patientType: '',
    patientStatus: '',
    noteStatus: '',
    dob: '',
    patientCountMin: 0,
    countStatus: '',
    facilityType: 'all',
  }
  const [dashboardFilters, setDashboardFilters] = useState(DEFAULT_FILTERS)
  const [dashboardPatients, setDashboardPatients] = useState([])
  const [dashboardDiagnosis, setDashboardDiagnosis] = useState([])
  const [dashboardMedications, setDashboardMedications] = useState([])
  const [dashboardPatientMedicationMap, setDashboardPatientMedicationMap] = useState({})
  const [dashboardAllFacilities, setDashboardAllFacilities] = useState([])
  const [dashboardMapDataReady, setDashboardMapDataReady] = useState(false)
  const [dashboardMedicationMapReadyKey, setDashboardMedicationMapReadyKey] = useState('')
  const dashboardPatientNotesRef = useRef({})
  const dashboardMedicationRecordCacheRef = useRef({})
  const dashboardPatientNoteFetchPromiseRef = useRef({})
  const dashboardPatientMedicationFetchPromiseRef = useRef({})
  const selectStatusOption = [
    { value: 'NewPatient', label: 'New Patient' },
    { value: 'Follow Up', label: 'Follow Up' },
    { value: 'Re Admit', label: 'Re Admit' },
  ]

  const patientTypeOptions = [
    { value: '', label: 'All Patients' },
    { value: 'Long Term', label: 'Long Term' },
    { value: 'Short Term', label: 'Short Term' },
  ]

  const uniqueNoteStatuses = ['Draft', 'Submitted', 'Pending', 'Completed']

  const toggleDropdown = () => {
    setIsDropdownOpen((prevState) => !prevState)
  }




  useEffect(() => {

    if (!selectedProviderID) return

    GetSelectedProviderFacility(selectedProviderID, selectedProvider)
  }, [refreshProviderFacilities])

  const handleSubmitAndClose = async () => {
    if (providerSelectedFacility.length === 0) {
      toast.error(
        'Please select at least one facility for credentialing',
        toastConfig
      )
      return
    }

    const Details = providerFullDetails?.Details

    if (!Details) {
      toast.error('Please select a provider', toastConfig)
      return
    }

    // Save provider first
    await handleSubmit()

    // Create one credentialing task per selected facility
    for (const facilityId of providerSelectedFacility) {
      const facility = facilities.find(
        (f) => String(f.ID) === String(facilityId)
      )

      if (!facility) continue

      const credentialTemplate = buildCredentialingTemplate({
        FacilityName: facility.Name,
        FacilityID: facility.ID,
        ProviderID: Details.ID,
        ProviderName: Details.Name,
        Description: facility.Type,
      })

      const taskPayload = {
        Title: facility.Name,
        Description: facility.Type,
        TaskType: 'Credentialing',
        FacilityID: facility.ID,
        FacilityName: facility.Name,
        ProviderID: Details.ID,
        ProviderName: Details.Name,
        Status: 'Pending',
        Priority: 'Medium',
        subtask: credentialTemplate.subtasks || [],
        SubtasksJson: credentialTemplate.subtasks || [],
      }

      const response = await providerCredentialingCRUD(
        JSON.stringify(taskPayload),
        '',
        'INSERT'
      )

      const parsed =
        typeof response?.d === 'string'
          ? JSON.parse(response.d)
          : response

      if (!parsed?.success) {
        toast.error(
          parsed?.message ||
          `Failed to create task for ${facility.Name}`
        )
        return
      }
    }

    toast.success('Credentialing tasks created successfully', toastConfig)

    setIsDropdownOpen(false)

    window.open(
      `/home/credentialing?providerId=provider-${Details.ID}`,
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleCredentialingConfirm = async () => {
    const Details = providerFullDetails?.Details

    try {
      // Save provider first
      await handleSubmit()

      const credentialTemplate = buildCredentialingTemplate({
        FacilityName: updateFacility.Name,
        FacilityID: updateFacility.ID,
        ProviderID: Details.ID,
        ProviderName: Details.Name,
        Description: updateFacility.Type,
      })

      // Create credentialing task
      const taskPayload = {
        Title: updateFacility.Name,
        Description: updateFacility.Type,
        TaskType: 'Credentialing',
        FacilityID: updateFacility.ID,
        FacilityName: updateFacility.Name,
        ProviderID: Details.ID,
        ProviderName: Details.Name,
        Status: 'Pending',
        Priority: 'Medium',
        subtask: credentialTemplate.subtasks || [],
        SubtasksJson: credentialTemplate.subtasks || [],
      }

      const response = await providerCredentialingCRUD(
        JSON.stringify(taskPayload),
        '',
        'INSERT'
      )

      const parsed =
        typeof response?.d === 'string'
          ? JSON.parse(response.d)
          : response

      if (!parsed?.success) {
        toast.error(
          parsed?.message ||
          `Failed to create credentialing task`
        )
        setShowCredentialingModal(false)
        return
      }

      toast.success('Credentialing task created successfully', toastConfig)
      setShowCredentialingModal(false)
      setVisible(false)

      // Redirect to credentialing page
      window.open(
        `/home/credentialing?providerId=provider-${Details.ID}`,
        '_blank',
        'noopener,noreferrer'
      )
    } catch (error) {
      console.error('Error creating credentialing task:', error)
      toast.error('Error creating credentialing task', toastConfig)
      setShowCredentialingModal(false)
    }
  }

  const handleCredentialingCancel = async () => {
    // User declined - just save without creating task
    await handleSubmit()
    setShowCredentialingModal(false)
    setVisible(false)
  }

  const handleModalSubmit = async () => {
    const Details = providerFullDetails?.Details

    // Check if status changed from "No" to "In Progress"
    if (selectedCredentialed === 'In Progress' && previousCredentialedStatus === 'No') {
      // Show confirmation modal
      setShowCredentialingModal(true)
    } else {
      // No condition met, just save normally
      await handleSubmit()
      setVisible(false)
    }
  }

  useEffect(() => {
    let a = getData()
  }, [])

  const handleToggle = () => {
    setIsToggled((prev) => !prev)
  }
  const credentialOption = [
    { value: 'Yes', label: 'Yes' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'No', label: 'No' },
  ]




  const getFacilityType = async () => {
    let a = await FacilityTypeFn('SELECT', '', '')
    let data = a.d
    data = JSON.parse(data)
    setOptions(data.Data.map((obj) => obj.Name))
  }

  const getData = async () => {
    setIsPageLoading(true)
    try {
      let a = await FacilityCRUD('SELECT', {})
      await getFacilityType()
      let data = JSON.parse(a.d)

      let temp3 = FacilityTypeFn('SELECT')
      temp3.then((res) => {
        try {
          let types = JSON.parse(res.d)

          let temp = types.Data.map((obj, i) => {
            return { value: obj['Name'], label: obj['Name'] }
          })
        } catch (e) { }
      })
    } catch (error) {
      console.log(error)
    } finally {
      setIsPageLoading(false)
    }
  }



  const AddFacilityFn = async (newFacility) => {
    rowData.current = newFacility
    await getFacilityType()
    setModalVisible(true)
  }

  const fetchFacility = async () => {
    if (dashboardMapView) {
      setDashboardMapDataReady(false)
      setDashboardPatientMedicationMap({})
      setDashboardMedicationMapReadyKey('')
      dashboardPatientNotesRef.current = {}
      dashboardMedicationRecordCacheRef.current = {}
      dashboardPatientNoteFetchPromiseRef.current = {}
      dashboardPatientMedicationFetchPromiseRef.current = {}
    }
    newFacility.current = []
    let temp2 = await FacilityCRUD('SELECTALL', {})
    let temp2FacilityList = JSON.parse(temp2.d)
    temp2FacilityList = temp2FacilityList.Data.filter((obj) => {
      if (obj.Lat) {
        return obj
      }
    })

    let temp3 = await FacilityTypeFn('SELECT')
    let types = JSON.parse(temp3.d)
    types = types.Data
    const facilitiesWithShape = temp2FacilityList.map((facility) => {
      try {
        let otherDetails = JSON.parse(facility['OtherDetails'])

        if (otherDetails && Object.keys(otherDetails).length > 0) {
          if (otherDetails['SisterCompany'] !== '') {
            facility['Sister'] = true
            facility['SisterCompany'] = otherDetails['SisterCompany']
          } else {
            facility['Sister'] = false
            facility['SisterCompany'] = ''
          }
        } else {
          facility['Sister'] = false
          facility['SisterCompany'] = ''
        }
      } catch (e) {
        facility['Sister'] = false
        facility['SisterCompany'] = ''
      }

      const typeObj = types.find((type) => type.Name === facility.Type)
      if (typeObj) {
        return { ...facility, Marker: typeObj.Marker }
      }
      return facility
    })

    setFacilityCounter(facilitiesWithShape.length)
    setFacilities(facilitiesWithShape)
    setDashboardAllFacilities(facilitiesWithShape)
    allFacilities.current = facilitiesWithShape
    setFacilityType(types)

    const uniqueCounties = [...new Set(temp2FacilityList.map((facility) => facility.County))]
    setCountyList(uniqueCounties)

    let providerDetails = await fetchPatientData('SELECT', 'Provider', '{}')
    providerDetails = JSON.parse(providerDetails?.d)
    setProviderList(providerDetails?.Data)

    if (dashboardMapView) {
      const [patientResult, diagnosisResult, medicationResult] = await Promise.allSettled([
        SavePatients('GetAll', '', '', ''),
        GetDiagnosisData(),
        MedicineCRUD('GET'),
      ])

      setDashboardPatients(patientResult.status === 'fulfilled' ? parseDashboardMapPayload(patientResult.value, []).map(unwrapDashboardMapPatient) : [])
      setDashboardDiagnosis(diagnosisResult.status === 'fulfilled' ? parseDashboardMapPayload(diagnosisResult.value, []) : [])
      setDashboardMedications(medicationResult.status === 'fulfilled' ? parseDashboardMapPayload(medicationResult.value, []) : [])
    }
  }

  function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371
    const dLat = (lat2 - lat1) * (Math.PI / 180)
    const dLng = (lng2 - lng1) * (Math.PI / 180)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  const onFilter = (type, n, ID = '', filName = '', userTriggered = false) => {
    setTypeName(filName)
    if (dashboardMapView) {
      if (type === 'type') {
        if (n === 'all') {
          setDashboardFilters((prev) => ({ ...prev, facilityType: 'all' }))
        } else if (n === 'radius') {
          setDashboardFilters((prev) => ({ ...prev, facilityType: filName || prev.facilityType }))
        } else if (n) {
          setDashboardFilters((prev) => ({ ...prev, facilityType: n }))
        }
      } else if (type === 'county') {
        setDashboardFilters((prev) => ({ ...prev, county: n || '' }))
      } else if (type === 'status') {
        setDashboardFilters((prev) => ({
          ...prev,
          countStatus: (prev.countStatus === n || n === 'all') ? '' : n,
        }))
      }
      return
    }
    let newFacilities = []
    if (type === 'type') {
      if (n === 'all') {
        newFacilities = allFacilities.current
      }
      //  else if (n === 'radius') {
      //   let latLang = providerList.filter((item) => item.ID === ID)
      //   const targetLat = parseFloat(latLang[0]?.Lat)
      //   const targetLng = parseFloat(latLang[0]?.Lng)
      //   newFacilities = allFacilities.current.filter((facility) => {
      //     const facilityLat = parseFloat(facility.Lat)
      //     const facilityLng = parseFloat(facility.Lng)
      //     const distance = haversineDistance(targetLat, targetLng, facilityLat, facilityLng)
      //     return distance <= Math.round(radius * 1609.34) / 1000
      //   })
      //   let filterFacilityId = selectedProviderFacilities.map((facility) => facility.FacilityID)

      //   let newAddFacility = allFacilities.current.filter((facility) => filterFacilityId.includes(facility.ID.toString()))
      //   if (isToggled) {
      //     newFacilities = newAddFacility
      //   } else {
      //     newFacilities = allFacilities.current
      //   }

      //   if (filName !== '') {
      //     newFacilities = newFacilities.filter((obj) => obj.Type === filName)
      //   }

      //   console.log('Facilities after radius filter:', newFacilities)
      // } 
      else if (n === 'radius') {
        let latLang = providerList.filter((item) => item.ID === ID)
        const targetLat = parseFloat(latLang[0]?.Lat)
        const targetLng = parseFloat(latLang[0]?.Lng)

        const facilitySource =
          facilities && facilities.length > 0
            ? facilities
            : allFacilities.current;
        // 1. radius filter first
        let radiusFiltered = facilitySource.filter((facility) => {
          const facilityLat = parseFloat(facility.Lat)
          const facilityLng = parseFloat(facility.Lng)

          const distance = haversineDistance(targetLat, targetLng, facilityLat, facilityLng)
          return distance <= Math.round(radius * 1609.34) / 1000  // ✅ FIX (see note below)
        })


        // if (isToggled) {
        //         newFacilities = radiusFiltered.includes(f.ID.toString())

        //       } else {
        //         newFacilities = allFacilities.current
        //       }

        if (isToggled) {
          const providerFacilityIds = selectedProviderFacilities.map(
            item => String(item.FacilityID)
          );

          newFacilities = radiusFiltered.filter(facility =>
            providerFacilityIds.includes(String(facility.ID))
          );
        } else {
          newFacilities = allFacilities.current;
        }

        // Apply Facility Type filter
        if (filName !== '') {
          newFacilities = newFacilities.filter(
            facility => facility.Type === filName
          );
        }

        console.log('Facilities after radius filter:', newFacilities, radiusFiltered)
      }
      else {
        newFacilities = allFacilities.current.filter((obj) => obj.Type === n)
      }
    }



    else if (type === 'status') {
      if (selectedProvider.length > 0) {

        if (activeStatusFilter === n) {
          setActiveStatusFilter(null)
          setFacilities(allFacilities.current)
          return
        }

        setActiveStatusFilter(n)

        // if (n === 'Credentialed' || n === 'Credentialing (In Progress)') {
        //   //const credentialFilter = n === 'Credentialed' ? 'Yes' : 'No'
        //   const credentialFilter =  

        if (n === 'Credentialed' || n === 'Credentialing (In Progress)') {
          const credentialFilter =
            n === 'Credentialed'
              ? 'Yes'
              : 'In Progress'; // or 'Credentialing (In Progress)' depending on DB

          const filteredProviderFacilities = selectedProviderFacilities.filter(
            obj =>
              String(obj.Credentialed).trim().toLowerCase() ===
              credentialFilter.trim().toLowerCase()
          );

          const facilityIdSet = new Set(
            filteredProviderFacilities.map(f => String(f.FacilityID))
          );

          newFacilities = allFacilities.current.filter(f =>
            facilityIdSet.has(String(f.ID))
          );
        }
        //   console.log('selectedProviderFacilities',selectedProviderFacilities)
        //   const filteredProviderFacilities = selectedProviderFacilities.filter((obj) => String(obj.Credentialed) === credentialFilter)
        //   const facilityIdSet = new Set(filteredProviderFacilities.map((f) => String(f.FacilityID)))
        //   newFacilities = allFacilities.current.filter((f) => facilityIdSet.has(String(f.ID)))

        // }
        else if (n === 'Not In') {
          const selectedIds = new Set(selectedProviderFacilities.map((f) => String(f.FacilityID)))
          newFacilities = allFacilities.current.filter((f) => !selectedIds.has(String(f.ID)))
        }
      } else {
        if (userTriggered) {
          toast.error('Please Select provider', toastConfig)
        }
        return
      }
    }

    else if (type === 'county') {
      newFacilities = allFacilities.current.filter((obj) => obj.County === n)
    }
    setFacilities(newFacilities)
  }

  useEffect(() => {
    if (isToggled) {
      if (typeName !== '') {
        onFilter('type', 'radius', selectedProvider[0]?.ID, typeName)
      } else {
        onFilter('type', 'radius', selectedProvider[0]?.ID)
      }
    } else {
      if (typeName !== '') {
        onFilter('type', 'radius', selectedProvider[0]?.ID, typeName)
      } else {
        onFilter('type', 'radius', selectedProvider[0]?.ID)
      }
    }
  }, [isToggled])

  const markerClick = (facility) => {
    if (!dashboardMapView && !selectedProvider.length > 0) return
    if (!facility) return

    const providerRecord = selectedProviderFacilities.find((p) => Number(p.FacilityID) === facility.ID)

    // Set credentialed flag based on provider record
    let isCredentialed = 'No' // default
    if (providerRecord) {
      if (providerRecord.Credentialed === 'Yes') {
        isCredentialed = 'Yes'
      } else if (providerRecord.Credentialed === 'In Progress') {
        isCredentialed = 'In Progress'
      } else if (providerRecord.Credentialed === 'No') {
        isCredentialed = 'No'
      }
    }

    setSelectedCredentialed(isCredentialed)
    setPreviousCredentialedStatus(isCredentialed)

    // Days — if stored in facility object or providerRecord
    setSelectedDays(providerRecord?.Days ?? facility?.Days ?? '')
    // Save full facility for edit modal
    setSelectedEditFacility(facility.Name)

    setUpdateFacility(facility)
    setActiveTab(selectedProvider.length > 0 ? 'changeFacilityDetails' : 'FacilityDetails')

    let type = facility['Type']
    let details = facility['Details']
    let questions = facilityType.find((obj) => obj.Name === type)
    if (details === '' || details === null || details === undefined) {
      details = null
    } else {
      setFormAnswer(JSON.parse(details))
    }

    // Open modal
    let schema = questions?.Details ? JSON.parse(questions.Details) : { components: [] }
    setSchema(schema)
    setVisible(true)
  }

  const submitFn = async () => {
    await fetchFacility()
    setModalVisible(false)
    setSearchValue('')
  }



  const handleProviderFacility = (event) => {
    if (event.target.disabled) return

    const facilityID = event.target.value

    setProviderSelectedFacility((prev) =>
      event.target.checked
        ? [...prev, facilityID]
        : prev.filter((id) => id !== facilityID)
    )
  }

  const handleProviderChange = (e) => {
    let providerDetails = e.target ? e.target.value : e.value
    if (providerDetails === '') {
      setSelectedProviderID(-1)
      setSelectedProviderName('')
      setSelectedProvider([])
      setSelectedProviderFacilities([])
      setProviderFullDetails(null)
      if (dashboardMapView) {
        setDashboardFilters((prev) => ({ ...prev, provider: '', countStatus: '' }))
      }
      if (!dashboardMapView) {
        handleFacilityChange({ value: selectedFacility.value, label: selectedFacility.label })
      }
    } else {
      const selectedProvider = providerList.find((provider) => provider.ID === parseInt(e.target ? e.target.value : e.value))
      setSelectedProviderID(selectedProvider ? selectedProvider.ID : -1)
      setSelectedProviderName(selectedProvider ? selectedProvider.Name : '')
      setSelectedProvider(selectedProvider ? [selectedProvider] : [])
      if (dashboardMapView) {
        setDashboardFilters((prev) => ({ ...prev, provider: selectedProvider ? selectedProvider.ID : '' }))
      }
    }
  }

  const GetSelectedProviderFacility = async (provider, providerInfo) => {
    let providerData
    providerData = await fetchPatientData('SELECTALL', 'Provider', JSON.stringify({ ID: provider }))
    console.log('providerData ', providerData)
    providerData = providerData?.d ? JSON.parse(providerData?.d) : [];
    console.log('Provider Data:', providerData)
    if (providerData) {
      const providerFacilities = providerData?.Facility || []
      const providerInsurance = providerData?.Insurance || []
      setSelectedProviderFacilities(providerFacilities)
      const facilityIDs = providerFacilities.map((item) => item.FacilityID)
      const finalFacilities = facilities.filter((facility) => facilityIDs.includes(facility.ID.toString()))
      let providerDetails = {
        Details: providerInfo[0],
        Insurances: providerInsurance,
        facilities: providerFacilities,
      }
      setProviderFullDetails(providerDetails)
    }
  }

  useEffect(() => {
    if (selectedProvider.length > 0 && selectedProviderID && selectedProviderID !== -1) {
      GetSelectedProviderFacility(selectedProviderID, selectedProvider)
    }
  }, [selectedProviderID, selectedProviderName, selectedProvider])

  const handleCountyChange = (e) => {
    const county = e.value
    setSelectedCounty(county)
    if (dashboardMapView) {
      setDashboardFilters((prev) => ({ ...prev, county }))
      return
    }
    if (county === '') {
      setFacilities(allFacilities.current)
    } else {
      onFilter('county', county)
    }
  }
  const handleFacilityChange = (e) => {
    setSelectedFacilities(e)
    const facility = e.value
    if (dashboardMapView) {
      setDashboardFilters((prev) => ({ ...prev, facilityType: facility || 'all' }))
      return
    }
    if (facility === 'all') {
      setFacilities(allFacilities.current)
      //onFilter("type", "Mixed");
    } else {
      if (selectedProvider.length > 0) {
        onFilter('type', 'radius', selectedProvider[0].ID, facility === 'all' ? '' : facility)
      } else {
        onFilter('type', facility)
      }
    }
  }

  const handlePracticingChange = (e) => {
    setPracticing(e)
    const practices = e.value
    if (dashboardMapView) {
      setDashboardFilters((prev) => ({ ...prev, practice: practices || '' }))
      return
    }
    if (practices === 'all') {
      onFilter('type', practices)
    } else {
      setFacilities(allFacilities.current)
      onFilter('type', 'Mixed')
    }
  }

  const handleMedicationChange = (e) => {
    setDashboardFilters((prev) => ({ ...prev, medication: e?.value || '' }))
  }

  const handleDiagnosisChange = (e) => {
    setDashboardFilters((prev) => ({ ...prev, diagnosis: e?.value || '' }))
  }

  const handlePatientCountChange = (event) => {
    setDashboardFilters((prev) => ({ ...prev, patientCountMin: Number(event.target.value) }))
  }

  const providerOptions = [
    { value: '', label: 'All Providers' },
    ...providerList
      .map((provider) => ({
        value: provider.ID,
        label: provider.Name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]
  const countryOption = [{ value: '', label: 'All County' }, ...countyList.map((country) => ({ value: country, label: country })).sort((a, b) => a.label.localeCompare(b.label))]
  const FacilityOption = [
    //{ value: "", label: "Select facility" },
    { value: 'all', label: 'All Facility Type' },
    ...facilityType.map((facility) => ({ value: facility.Name, label: facility.Name })),
  ]

  const selectedProviderOption = providerOptions.find((option) => normalizeDashboardMapId(option.value) === normalizeDashboardMapId(dashboardFilters.provider)) || providerOptions[0]
  const selectedCountyOption = countryOption.find((option) => normalizeDashboardMapText(option.value) === normalizeDashboardMapText(dashboardFilters.county)) || countryOption[0]
  const selectedFacilityTypeOption = FacilityOption.find((option) => normalizeDashboardMapText(option.value) === normalizeDashboardMapText(dashboardFilters.facilityType)) || FacilityOption[0]

  const medicationOptions = useMemo(() => {
    const optionMap = new Map()
    dashboardMedications.forEach((item) => {
      const medication =
        typeof item?.Data === 'string'
          ? (() => {
              try {
                return JSON.parse(item.Data)
              } catch (e) {
                return item?.Data || {}
              }
            })()
          : item?.Data || item || {}

      const name = String(medication?.MedicineName || medication?.Name || medication?.name || medication?.label || medication?.medicationName || medication?.MedicationName || '').trim()
      const otherName = String(
        medication?.OtherMedicineName || medication?.otherName || medication?.OtherName || medication?.othername || ''
      ).trim()

      if (!name && !otherName) return

      const label = otherName ? `${name} (${otherName})` : name
      const value = item.ID || item.Id || item.MedicationID || item.medicationID || label

      optionMap.set(normalizeDashboardMapId(value || label), {
        value: value || label,
        label,
        name,
        otherName,
      })
    })
    return [{ value: '', label: 'Any medication' }, ...Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label))]
  }, [dashboardMedications])

  const diagnosisOptions = useMemo(() => {
    const optionMap = new Map()
    dashboardDiagnosis.forEach((diagnosis) => {
      const label = diagnosis?.DiagnosisName || diagnosis?.Name || diagnosis?.name || diagnosis?.label
      const value = diagnosis?.id || diagnosis?.ID || diagnosis?.DiagnosisID || diagnosis?.diagnosisID || label
      if (label) optionMap.set(normalizeDashboardMapId(value || label), { value: value || label, label })
    })
    return [{ value: '', label: 'Any diagnosis' }, ...Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label))]
  }, [dashboardDiagnosis])

  const dashboardPatientIds = useMemo(
    () => Array.from(new Set(dashboardPatients.map(getDashboardMapPatientId).filter(Boolean))).sort(),
    [dashboardPatients],
  )
  const dashboardPatientIdsKey = dashboardPatientIds.join('|')
  const dashboardMedicationMapReady = !dashboardPatientIdsKey || dashboardMedicationMapReadyKey === dashboardPatientIdsKey

  const getPatientFacilityKeys = (patient) => {
    const keys = [
      patient?.FacilityID,
      patient?.facilityID,
      patient?.FacilityId,
      patient?.facilityId,
      patient?.Facility_ID,
      patient?.facility_id,
      patient?.Facility,
      patient?.facility,
      patient?.FacilityName,
      patient?.facilityName,
      patient?.Facility_Name,
      patient?.facility_name,
      patient?.facility?.Name,
      patient?.facility?.name,
      patient?.facility?.label,
      patient?.facility?.value,
      patient?.facility?.ID,
      patient?.facility?.id,
    ]
    return keys.flatMap((key) => {
      const parsed = parseDashboardMapJsonValue(key)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return [
          parsed.FacilityID,
          parsed.facilityID,
          parsed.ID,
          parsed.id,
          parsed.value,
          parsed.FacilityName,
          parsed.facilityName,
          parsed.Name,
          parsed.name,
          parsed.label,
        ]
      }
      return [key]
    }).flatMap((key) => {
      const idKey = normalizeDashboardMapId(key)
      const compareKey = normalizeDashboardMapCompare(key)
      return [idKey, compareKey].filter(Boolean)
    })
  }

  const patientFacilityMap = useMemo(() => {
    const map = new Map()
    dashboardPatients.forEach((patient) => {
      getPatientFacilityKeys(patient).forEach((key) => {
        if (!map.has(key)) map.set(key, [])
        map.get(key).push(patient)
      })
    })
    return map
  }, [dashboardPatients])

  const getPatientsForFacility = (facility) => {
    const facilityKeys = [
      facility?.ID,
      facility?.FacilityID,
      facility?.facilityID,
      facility?.Name,
      facility?.FacilityName,
      facility?.name,
    ].flatMap((key) => {
      const idKey = normalizeDashboardMapId(key)
      const compareKey = normalizeDashboardMapCompare(key)
      return [idKey, compareKey].filter(Boolean)
    })
    const patients = new Map()
    facilityKeys.forEach((key) => {
      ; (patientFacilityMap.get(key) || []).forEach((patient, index) => {
        const patientKey = getDashboardMapPatientId(patient) || `${key}-${index}`
        patients.set(normalizeDashboardMapId(patientKey), patient)
      })
    })
    return Array.from(patients.values())
  }

  const patientHasDiagnosis = (patient, diagnosisName) => {
    if (!diagnosisName) return true
    const selectedOption = diagnosisOptions.find((option) =>
      String(option.value) === String(diagnosisName) ||
      String(option.label) === String(diagnosisName)
    )
    const selectedValues = [
      diagnosisName,
      selectedOption?.label,
      selectedOption?.value,
    ].map(normalizeDashboardMapCompare).filter(Boolean)
    const diagnosisValue = patient?.Diagnosis || patient?.diagnosis || patient?.DiagnosisName || patient?.diagnosisName || patient?.PrimaryDiagnosis

    // Robust fallback: Check if the stringified diagnoses list contains any of the selected values directly.
    // This ensures patients with multiple diagnoses (e.g. "Alcohol abuse, Dementia") match 100% of the time.
    const rawString = normalizeDashboardMapCompare(
      typeof diagnosisValue === 'object' && diagnosisValue !== null
        ? JSON.stringify(diagnosisValue)
        : diagnosisValue
    )
    if (selectedValues.some((val) => rawString.includes(val))) {
      return true
    }

    const fields = Array.isArray(diagnosisValue)
      ? diagnosisValue.flatMap((entry) =>
        typeof entry === 'object'
          ? [entry?.label, entry?.value, entry?.Name, entry?.name, entry?.id, entry?.ID]
          : [entry],
      )
      : typeof diagnosisValue === 'object' && diagnosisValue !== null
        ? [diagnosisValue?.label, diagnosisValue?.value, diagnosisValue?.Name, diagnosisValue?.name, diagnosisValue?.id, diagnosisValue?.ID]
        : String(diagnosisValue || '')
          .split(',')
          .map((entry) => entry.trim())

    return fields
      .map(normalizeDashboardMapCompare)
      .filter(Boolean)
      .some((field) => selectedValues.some((selectedValue) => field === selectedValue || field.includes(selectedValue) || selectedValue.includes(field)))
  }

  const patientHasMedication = (patient, medicationName) => {
    if (!medicationName) return true
    const selectedOption = medicationOptions.find((option) =>
      String(option.value) === String(medicationName) ||
      String(option.name) === String(medicationName) ||
      String(option.label) === String(medicationName)
    )
    const medicationValues = [
      medicationName,
      selectedOption?.label,
      selectedOption?.value,
      selectedOption?.name,
      selectedOption?.otherName,
    ].flatMap(getDashboardMapMedicationNames).map(normalizeDashboardMapCompare).filter(Boolean)
    const patientId = getDashboardMapPatientId(patient)
    const medicationRecords = dashboardPatientMedicationMap[patientId] || []
    const fields = [
      patient?.Medication,
      patient?.Medications,
      patient?.MedicationName,
      patient?.medicationName,
      ...medicationRecords.flatMap(getDashboardMapMedicationNames),
    ].flatMap(getDashboardMapMedicationNames)
    return fields
      .map(normalizeDashboardMapCompare)
      .filter(Boolean)
      .some((field) => medicationValues.some((selectedValue) => field.includes(selectedValue) || selectedValue.includes(field)))
  }

  const getDashboardMapMedicationMetadataId = (note) => {
    const metadata = note?.data?.metadata || note?.data?.metaData || note?.metadata || note?.metaData || {}
    return metadata?.medId || metadata?.medicationID || metadata?.medicationId || ''
  }

  const fetchDashboardMapNotesForPatientIds = useCallback(async (patientIds) => {
    const uniquePatientIds = Array.from(new Set((patientIds || []).filter(Boolean).map(String)))
    const idsToFetch = uniquePatientIds.filter((patientID) => !dashboardPatientNotesRef.current?.[patientID])

    if (idsToFetch.length === 0) return {}

    const fetchNoteForPatient = (patientID) => {
      if (dashboardPatientNoteFetchPromiseRef.current[patientID]) {
        return dashboardPatientNoteFetchPromiseRef.current[patientID]
      }

      const promise = (async () => {
        try {
          const response = await PatientNoteDataCRUD('GET', patientID)
          const parsed = JSON.parse(response?.d || '{}')
          const notes = Array.isArray(parsed?.Data)
            ? parsed.Data.map((item) => ({
                ...item,
                data: item?.data
                  ? typeof item.data === 'string'
                    ? JSON.parse(item.data)
                    : item.data
                  : {},
              }))
            : []
          return { patientID, notes }
        } catch (error) {
          console.error('Error fetching dashboard map patient notes for', patientID, error)
          return { patientID, notes: [] }
        } finally {
          delete dashboardPatientNoteFetchPromiseRef.current[patientID]
        }
      })()

      dashboardPatientNoteFetchPromiseRef.current[patientID] = promise
      return promise
    }

    const NOTE_BATCH_SIZE = 128
    const results = []
    for (let index = 0; index < idsToFetch.length; index += NOTE_BATCH_SIZE) {
      const batch = idsToFetch.slice(index, index + NOTE_BATCH_SIZE)
      const batchResults = await Promise.all(batch.map(fetchNoteForPatient))
      results.push(...batchResults)
    }

    const fetchedNotes = {}
    results.forEach(({ patientID, notes }) => {
      if (patientID) fetchedNotes[patientID] = notes
    })

    dashboardPatientNotesRef.current = {
      ...dashboardPatientNotesRef.current,
      ...fetchedNotes,
    }

    return fetchedNotes
  }, [])

  const getFilteredPatientsForFacility = useCallback((facility) => {
    const allPatients = getPatientsForFacility(facility)
    return allPatients.filter((patient) => {
      if (dashboardFilters.diagnosis && !patientHasDiagnosis(patient, dashboardFilters.diagnosis)) return false
      if (dashboardFilters.medication && !patientHasMedication(patient, dashboardFilters.medication)) return false

      if (dashboardFilters.patientType) {
        const typeFilter = String(dashboardFilters.patientType).trim().toLowerCase()
        const ptType = patient?.PatientType || patient?.patientType || ''
        if (String(ptType).trim().toLowerCase() !== typeFilter) return false
      }

      if (dashboardFilters.patientStatus) {
        const statusFilter = String(dashboardFilters.patientStatus).trim().toLowerCase()
        const ptStatus = patient?.Status || patient?.status || ''
        if (String(ptStatus).trim().toLowerCase() !== statusFilter) return false
      }

      if (dashboardFilters.noteStatus) {
        const noteFilter = String(dashboardFilters.noteStatus).trim().toLowerCase()
        const nStatus = patient?.NoteStatus || patient?.noteStatus || ''
        if (String(nStatus).trim().toLowerCase() !== noteFilter) return false
      }

      if (dashboardFilters.dob) {
        const dobFilter = String(dashboardFilters.dob).trim()
        const pDob = patient?.DOB || patient?.dob || ''
        if (!String(pDob).includes(dobFilter) && !dobFilter.includes(String(pDob))) return false
      }

      return true
    })
  }, [
    patientFacilityMap,
    dashboardFilters.diagnosis,
    dashboardFilters.medication,
    dashboardFilters.patientType,
    dashboardFilters.patientStatus,
    dashboardFilters.noteStatus,
    dashboardFilters.dob,
    patientHasDiagnosis,
    patientHasMedication,
  ])

  const facilitiesWithPatientCounts = useMemo(() => {
    const source = dashboardMapView && dashboardAllFacilities.length ? dashboardAllFacilities : allFacilities.current.length ? allFacilities.current : facilities
    return source.map((facility) => {
      const filteredPatients = getFilteredPatientsForFacility(facility)
      return {
        ...facility,
        patientCount: filteredPatients.length,
        filteredPatients: filteredPatients,
      }
    })
  }, [dashboardMapView, dashboardAllFacilities, facilities, facilityCounter, getFilteredPatientsForFacility])

  const dashboardMapFacilities = useMemo(() => {
    if (!dashboardMapView) return facilities
    if (!dashboardMapDataReady || !dashboardMedicationMapReady) return []

    const selectedProviderFacilityIds = new Set(selectedProviderFacilities.map((facility) => normalizeDashboardMapId(facility.FacilityID)))
    const selectedProviderRecord = selectedProvider[0]
    const targetLat = parseFloat(selectedProviderRecord?.Lat)
    const targetLng = parseFloat(selectedProviderRecord?.Lng)
    const radiusKm = Math.round(Number(radius || 0) * 1609.34) / 1000

    return facilitiesWithPatientCounts.filter((facility) => {
      // Dashboard Map View is patient-backed. Do not render standalone
      // facility-map markers that have no dashboard patients.
      if (facility.patientCount === 0) return false

      if (dashboardFilters.patientCountMin > 0 && Number(facility.patientCount || 0) < Number(dashboardFilters.patientCountMin)) return false

      if (dashboardFilters.facilityType && dashboardFilters.facilityType !== 'all') {
        const selectedFacilityType = normalizeDashboardMapText(dashboardFilters.facilityType)
        const facilityTypeName = normalizeDashboardMapText(facility.Type || facility.type)
        if (facilityTypeName !== selectedFacilityType) return false
      }

      if (selectedProviderRecord && Number.isFinite(targetLat) && Number.isFinite(targetLng)) {
        const facilityLat = parseFloat(facility.Lat)
        const facilityLng = parseFloat(facility.Lng)
        const distance = haversineDistance(targetLat, targetLng, facilityLat, facilityLng)
        if (Number.isFinite(distance) && distance > radiusKm) return false

        if (isToggled && !selectedProviderFacilityIds.has(getDashboardMapFacilityId(facility))) return false
      }

      if (dashboardFilters.countStatus) {
        const count = Number(facility.patientCount || 0)
        if (count === 0) return false
        if (dashboardFilters.countStatus === '<10' && count >= 10) return false
        if (dashboardFilters.countStatus === '10-20' && (count < 10 || count > 20)) return false
        if (dashboardFilters.countStatus === '>20' && count <= 20) return false
      }

      return true
    })
  }, [
    dashboardMapView,
    dashboardMapDataReady,
    dashboardMedicationMapReady,
    facilities,
    facilitiesWithPatientCounts,
    dashboardFilters,
    selectedProvider,
    selectedProviderFacilities,
    radius,
    isToggled,
  ])

  const dashboardRangePercent = Math.min(100, Math.max(0, Number(dashboardFilters.patientCountMin || 0)))


  const handleSubmit = async () => {
    console.log('Submitting with Credentialed:', selectedCredentialed, 'Days:', selectedDays)
    try {
      const Details = providerFullDetails?.Details
      const Insurances = providerFullDetails?.Insurances || []

      const existingFacility = providerFullDetails?.facilities.find(
        (item) => item.FacilityID == updateFacility.ID
      )

      let FinalFacilityList = []

      if (existingFacility) {
        FinalFacilityList = providerFullDetails.facilities.map((item) =>
          item.FacilityID == updateFacility.ID
            ? {
              ...item,
              Credentialed: selectedCredentialed,
              Days: selectedDays,
            }
            : item
        )
      } else {
        FinalFacilityList = [
          ...providerFullDetails.facilities,
          {
            FacilityID: updateFacility.ID,
            Days: selectedDays,
            Credentialed: selectedCredentialed,
          },
        ]
      }

      const providerData = {
        Name: Details.Name,
        Gender: Details.Gender,
        DOB: Details.DOB,
        Address: Details.Address,
        contractStartDate: Details.contractStartDate,
        AddressDetails: Details.AddressDetails,
        Lat: Details.Lat,
        Lng: Details.Lng,
        UserId: Details.UserId,
        Insurances: JSON.stringify(Insurances),
        Facilities: JSON.stringify(FinalFacilityList),
      }

      await fetchPatientData(
        'UPDATE',
        'Provider',
        JSON.stringify({
          ...providerData,
          ID: Details.ID,
        })
      )
      setRefreshProviderFacilities(prev => !prev)

      toast.success('Successfully Saved', toastConfig)

    } catch (error) {
      console.error(error)
    }
  }
  const practicingOption = [
    { value: '', label: 'Select Practice' },
    { value: 'all', label: 'Cerebrum Health Care' },
  ]
  useEffect(() => {
    fetchFacility().then(() => {
      // Ensure all facilities are shown by default
      setSelectedFacilities(FacilityOption[0]) // 'ALL'
      setFacilities(allFacilities.current)
    })
  }, [])

  const [practicing, setPracticing] = useState(practicingOption.length > 0 ? practicingOption[1] : null)

  useEffect(() => {
    if (!dashboardMapView) return

    if (dashboardPatients.length === 0) {
      setDashboardPatientMedicationMap({})
      setDashboardMedicationMapReadyKey('')
      setDashboardMapDataReady(true)
      return
    }

    let cancelled = false
    const loadKey = dashboardPatientIdsKey

    const loadDashboardPatientMedications = async () => {
      setDashboardMapDataReady(false)
      const medicationEntries = {}
      await fetchDashboardMapNotesForPatientIds(dashboardPatientIds)

      for (let index = 0; index < dashboardPatients.length; index += 64) {
        const patientBatch = dashboardPatients.slice(index, index + 64)
        await Promise.all(patientBatch.map(async (patient) => {
          const patientId = getDashboardMapPatientId(patient)
          if (!patientId) return

          try {
            const patientNotesData = dashboardPatientNotesRef.current?.[patientId] || []
            const medicationIds = Array.from(
              new Set(
                patientNotesData
                  .map((note) => String(getDashboardMapMedicationMetadataId(note) || '').trim())
                  .filter(Boolean),
              ),
            )

            if (medicationIds.length === 0) {
              medicationEntries[patientId] = []
              return
            }

            let medicationRecords = dashboardMedicationRecordCacheRef.current[patientId]
            if (!medicationRecords) {
              if (dashboardPatientMedicationFetchPromiseRef.current[patientId]) {
                medicationRecords = await dashboardPatientMedicationFetchPromiseRef.current[patientId]
              } else {
                const promise = (async () => {
                  try {
                    const response = await PatientMedications('GET', patientId)
                    return parseDashboardMapPayload(response, []).map((row) => ({
                      ...row,
                      data: typeof row?.data === 'string' ? parseDashboardMapJsonValue(row.data) : Array.isArray(row?.data) ? row.data : [],
                    }))
                  } catch (error) {
                    console.error('Unable to load patient medications for dashboard map:', error)
                    return []
                  } finally {
                    delete dashboardPatientMedicationFetchPromiseRef.current[patientId]
                  }
                })()
                dashboardPatientMedicationFetchPromiseRef.current[patientId] = promise
                medicationRecords = await promise
                dashboardMedicationRecordCacheRef.current[patientId] = medicationRecords
              }
            }

            medicationEntries[patientId] = medicationIds.flatMap((medicationId) => {
              const matchingRecord = medicationRecords.find((record) => String(record?.id) === String(medicationId))
              if (!matchingRecord || !Array.isArray(matchingRecord.data)) return []

              return matchingRecord.data.map((entry) => ({
                name: entry?.name || entry?.medicationName || '',
                otherName: entry?.otherName || entry?.OtherName || entry?.othername || '',
                label: entry?.label || '',
              }))
            })
          } catch (error) {
            console.error('Unable to resolve dashboard map medications for patient:', error)
            medicationEntries[patientId] = []
          }
        }))
      }

      if (!cancelled) {
        setDashboardPatientMedicationMap(medicationEntries)
        setDashboardMedicationMapReadyKey(loadKey)
        setDashboardMapDataReady(true)
      }
    }

    loadDashboardPatientMedications()

    return () => {
      cancelled = true
    }
  }, [dashboardMapView, dashboardPatients, dashboardPatientIds, dashboardPatientIdsKey, fetchDashboardMapNotesForPatientIds])

  useEffect(() => {
    if (selectedProvider.length > 0) {
      if (typeName !== '') {
        onFilter('type', 'radius', selectedProvider[0].ID, typeName)
      } else {
        onFilter('type', 'radius', selectedProvider[0].ID)
      }
    }
  }, [radius])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const validateFacilityList = () => {
    const facilityList = getFacilityList()

    if (!facilityList || facilityList.length === 0) {
      toast.error('No facilities available', toastConfig)
      return null
    }

    return facilityList
  }

  const handleDownload = () => {
    const facilityList = validateFacilityList()

    if (!facilityList) return

    const text = facilityList
      .map(
        f =>
          `${f.Name}`
      )
      .join('\n')

    const blob = new Blob([text], {
      type: 'text/plain;charset=utf-8',
    })

    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'facilities.txt'
    link.click()

    URL.revokeObjectURL(url)
  }

  const getFacilityList = () => {
    const sourceFacilities = dashboardMapView ? dashboardMapFacilities : facilities
    if (!selectedProvider?.length) return sourceFacilities

    const targetLat = parseFloat(selectedProvider[0]?.Lat)
    const targetLng = parseFloat(selectedProvider[0]?.Lng)

    return sourceFacilities.filter((facility) => {
      const facilityLat = parseFloat(facility.Lat)
      const facilityLng = parseFloat(facility.Lng)

      const distance = haversineDistance(
        targetLat,
        targetLng,
        facilityLat,
        facilityLng
      )

      return distance <= Math.round(radius * 1609.34) / 1000
    })
  }
  const filteredFacilities = facilities.filter((facility) =>
    facility.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const mapFacilities = (dashboardMapView ? dashboardMapFacilities : facilities).filter((facility) => {
    const lat = parseFloat(facility?.Lat)
    const lng = parseFloat(facility?.Lng)
    return Number.isFinite(lat) && Number.isFinite(lng)
  })

  return (
    <>
      <div className={`facility-map-theme dashboard-dark-theme patient-theme-page patient-page-wrapper ${dashboardMapView ? 'dashboard-map-view' : ''}`}>
        <CRow className="mb-2 facility-map-theme__toolbar align-items-center flex-nowrap">
          {/* <CCol sm={3} className="facility-map-theme__search-col">
            <AutoComplete initialValue={searchValue} AddFacilityFn={AddFacilityFn}></AutoComplete>
          </CCol>
          <CCol sm={2} className="facility-map-theme__radius-col">
            <div className="facility-map-theme__radius-field" style={{ display: 'flex', alignItems: 'center' }}>
              <CFormLabel style={{ flex: 1, marginRight: '8px' }}>Radius</CFormLabel>
              <CFormInput
                className="w-50"
                value={radius}
                onChange={(e) => {
                  setRadius(e.target.value)
                }}
              ></CFormInput>
              <CFormLabel className="pt-1 ps-2" style={{ flex: 1 }}>
                Miles
              </CFormLabel>
            </div>
          </CCol> */}
          {/* <CCol sm={5} className="d-flex justify-content-end align-items-center facility-map-theme__radius-menu-col">
            <div ref={dropdownRef} className="facility-map-theme__radius-menu-anchor" style={{ position: 'relative', display: 'inline-block' }}>
              <CButton
                onClick={toggleDropdown}
                color=""
                variant="outline"
                className={`d-flex justify-content-between align-items-center no-hover facility-map-theme__radius-button ${selectedProvider.length <= 0 ? "disabled" : ""
                  }`}
                style={{
                  width: "250px",
                  height: "38px",
                  background: "white",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  margin: "0 10px",
                  cursor: selectedProvider.length <= 0 ? "not-allowed" : "pointer",
                  pointerEvents: selectedProvider.length <= 0 ? "none" : "auto",
                  fontWeight: 500,
                }}
              >
                <span>Facilities within Radius</span>

                <span
                  style={{
                    transition: "transform .2s ease",
                    transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    fontSize: "12px",
                  }}
                >
                  ▼
                </span>
              </CButton>
              {isDropdownOpen && (
                <div
                  className="facility-map-theme__radius-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 5,
                    width: '500px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    boxShadow: '0px 4px 8px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    borderRadius: '7px'
                  }}
                >
                  <div className="container p-2">

                    <div className="d-flex justify-content-end align-items-center gap-2  p-2">


                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Facilities"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <CButton
                        color="primary"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const facilityList = validateFacilityList();

                          if (!facilityList) return;

                          const text = facilityList
                            .map(f => `${f.Name}`)
                            .join('\n');

                          navigator.clipboard.writeText(text);
                          toast.success('Copied to clipboard', toastConfig);
                        }}
                      >
                        <Copy size={15} className="me" />

                      </CButton>

                      <CButton
                        color="success"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          handleDownload();
                          setIsDropdownOpen(false);
                        }}
                      >
                        <Download size={15} className="me" />

                      </CButton>
                    </div>

                    <ul
                      className="list-unstyled p-2 bg-white rounded facility-map-theme__facility-list"
                      style={{ overflow: 'auto', height: '300px' }}
                    >
                      <ul className="list-unstyled bg-white rounded facility-map-theme__facility-list-inner">

                        {getFacilityList()
                          .filter((facility) =>
                            facility.Name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map((facility) => {
                            const providerRecord = selectedProviderFacilities.find(
                              (p) => String(p.FacilityID) === String(facility.ID)
                            )
                            console.log('Facility:', facility.Name, 'Provider Record:', providerRecord)
                            let status = 'Not In';

                            if (providerRecord) {
                              if (providerRecord.Credentialed === 'Yes') {
                                status = 'Credentialed';
                              } else if (
                                providerRecord.Credentialed === 'In Progress' ||
                                providerRecord.Credentialed === 'Credentialing (In Progress)'
                              ) {
                                status = 'Credentialing (In Progress)';
                              } else {
                                status = providerRecord.Credentialed;
                              }
                            }

                            // const isChecked = status !== 'Not In'
                            const isDisabled =
                              providerRecord?.Credentialed === 'Yes' ||
                              providerRecord?.Credentialed === 'In Progress';
                            const isChecked = providerSelectedFacility.includes(
                              String(facility.ID)
                            )
                            const getStatusColor = (status) => {
                              switch (status) {
                                case "Credentialed":
                                  return "green";

                                case "Credentialing (In Progress)":
                                case "In Progress":
                                  return "yellow";

                                case "Not In":
                                  return "red";

                                default:
                                  return "red";
                              }

                            };
                            return (
                              <li
                                key={facility.ID}
                                className="mb-2 p-2 bg-white rounded d-flex justify-content-between  align-items-center facility-map-theme__facility-item"  >

                                <label className="d-flex align-items-center gap-2 m-0">
                                  <input
                                    type="checkbox"
                                    value={facility.ID}
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    onChange={handleProviderFacility}
                                  />
                                  {facility.Name}
                                </label>

                                <span
                                  title={status}
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "50%",
                                    backgroundColor: getStatusColor(status),
                                    display: "inline-block",
                                  }}
                                ></span>
                              </li>
                            )
                          })}
                      </ul>
                    </ul>

                    <div className="d-flex justify-content-end align-items-center gap-2  p-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Close
                      </button>

                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleSubmitAndClose}
                      >
                        Start Credentialing
                      </button>
                    </div>
                  </div>
                </div>
              )}
          </CCol> */}

          <CCol className="flex-grow-1"></CCol>
          <CCol sm={1} className="d-flex justify-content-end align-items-center facility-map-theme__filter-col dashboard-map-filter-anchor">
            {dashboardMapView ? (
              <>
                <CButton
                  color="primary"
                  variant="outline"
                  className="no-hover facility-map-theme__filter-button dashboard-map-view__filter-button d-flex align-items-center"
                  onClick={() => setDashboardFilterVisible((prev) => !prev)}
                >
                  <FiFilter size={16} className="me-2" />
                  Filter
                </CButton>
                {dashboardFilterVisible && (
                  <div className="dashboard-map-filter-popover dashboard-map-filter-modal">
                    <div className="dashboard-map-filter-modal__body">
                      <div className="dashboard-map-filter-modal__grid">
                        <div>
                          <CFormLabel>Select Diagnosis</CFormLabel>
                          <AppSelect
                            placeholder="Select"
                            name="diagnosis"
                            classNamePrefix="facility-map-select"
                            options={diagnosisOptions.filter((o) => o.value !== '')}
                            value={dashboardFilters.diagnosis ? diagnosisOptions.find((opt) => opt.label === dashboardFilters.diagnosis || opt.value === dashboardFilters.diagnosis) || null : null}
                            isClearable
                            onChange={(selectedOption) => {
                              const val = selectedOption?.label || ''
                              setDashboardFilters((prev) => ({ ...prev, diagnosis: val }))
                            }}
                          />
                        </div>

                        <div>
                          <CFormLabel>Select Medication</CFormLabel>
                          <AppSelect
                            placeholder="Select"
                            name="medication"
                            classNamePrefix="facility-map-select"
                            options={medicationOptions.filter((o) => o.value !== '')}
                            value={dashboardFilters.medication ? medicationOptions.find((opt) => opt.name === dashboardFilters.medication || opt.label === dashboardFilters.medication || opt.value === dashboardFilters.medication) || null : null}
                            isClearable
                            onChange={(selectedOption) => {
                              const val = selectedOption?.name || selectedOption?.label || ''
                              setDashboardFilters((prev) => ({ ...prev, medication: val }))
                            }}
                          />
                        </div>

                        <div>
                          <CFormLabel>Select Patient Type</CFormLabel>
                          <AppSelect
                            placeholder="All Patients"
                            name="patientType"
                            classNamePrefix="facility-map-select"
                            options={patientTypeOptions}
                            value={patientTypeOptions.find((opt) => opt.value === dashboardFilters.patientType) || patientTypeOptions[0]}
                            onChange={(selectedOption) => {
                              const val = selectedOption?.value || ''
                              setDashboardFilters((prev) => ({ ...prev, patientType: val }))
                            }}
                          />
                        </div>

                        <div>
                          <CFormLabel>Select Patient Status</CFormLabel>
                          <AppSelect
                            placeholder="Select"
                            name="patientStatus"
                            classNamePrefix="facility-map-select"
                            options={selectStatusOption}
                            value={selectStatusOption.find((opt) => opt.value === dashboardFilters.patientStatus) || null}
                            isClearable
                            onChange={(selectedOption) => {
                              const val = selectedOption?.value || ''
                              setDashboardFilters((prev) => ({ ...prev, patientStatus: val }))
                            }}
                          />
                        </div>

                        <div>
                          <CFormLabel>Select Note Status</CFormLabel>
                          <AppSelect
                            placeholder="Select"
                            name="noteStatus"
                            classNamePrefix="facility-map-select"
                            options={uniqueNoteStatuses.map((s) => ({ value: s, label: s }))}
                            value={dashboardFilters.noteStatus ? { value: dashboardFilters.noteStatus, label: dashboardFilters.noteStatus } : null}
                            isClearable
                            onChange={(selectedOption) => {
                              const val = selectedOption?.value || ''
                              setDashboardFilters((prev) => ({ ...prev, noteStatus: val }))
                            }}
                          />
                        </div>

                        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                          <CFormLabel>Select DOB</CFormLabel>
                          <div>
                            <CustomDatePickingFormat
                              value={dashboardFilters.dob || ''}
                              aria-describedby="exampleFormControlInputHelpInline"
                              onChange={(e) => {
                                setDashboardFilters((prev) => ({ ...prev, dob: e }))
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* <div className="dashboard-map-filter-modal__range">
                        <div className="dashboard-map-filter-modal__range-heading">
                          <span>Patient Range</span>
                        </div>
                        <div className="dashboard-map-filter-modal__range-track-wrap">
                          <span
                            className="dashboard-map-filter-modal__range-bubble"
                            style={{ left: `${dashboardRangePercent}%` }}
                          >
                            {dashboardFilters.patientCountMin}+
                          </span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={dashboardFilters.patientCountMin}
                            onChange={handlePatientCountChange}
                            className="dashboard-map-filter-modal__range-input"
                            style={{
                              background: `linear-gradient(to right, var(--ui-color-primary, #2d83dc) 0%, var(--ui-color-primary, #2d83dc) ${dashboardRangePercent}%, var(--dashboard-map-range-empty, rgba(255,255,255,0.2)) ${dashboardRangePercent}%, var(--dashboard-map-range-empty, rgba(255,255,255,0.2)) 100%)`,
                            }}
                          />
                        </div>
                        <div className="dashboard-map-filter-modal__range-marks">
                          <span>0</span>
                          <span>25</span>
                          <span>50</span>
                          <span>75</span>
                          <span>100+</span>
                        </div>
                      </div> */}
                    </div>
                    <div className="dashboard-map-filter-modal__footer d-flex gap-2 p-3">
                      <CButton
                        color="secondary"
                        style={{ flex: 1 }}
                        onClick={() => setDashboardFilterVisible(false)}
                      >
                        Close
                      </CButton>
                      <CButton
                        color="primary"
                        style={{ flex: 1 }}
                        onClick={() => {
                          setDashboardFilters(DEFAULT_FILTERS)
                        }}
                      >
                        Reset
                      </CButton>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <CDropdown>
                <CDropdownToggle
                  color="primary"
                  variant="outline"
                  className="no-hover facility-map-theme__filter-button d-flex align-items-center"
                  style={{ margin: '0 10px 0 10px' }}
                >
                  <FiFilter size={16} className="me-2" />
                  Filter
                </CDropdownToggle>
                <CDropdownMenu className="facility-map-theme__filter-menu" style={{ width: '300px', top: '45px', right: '0' }}>
                  <CDropdownItem href="#" className="no-hover bg-secondary facility-map-theme__filter-item">
                    {' '}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                      <CFormLabel style={{ flex: 1, marginRight: '8px' }}>Practice</CFormLabel>
                      <Select options={practicingOption} onChange={handlePracticingChange} value={practicing} className="w-100" classNamePrefix="facility-map-select" closeMenuOnSelect={false} isMulti={false} defaultValue={practicingOption[0]} isSearchable={true} />
                    </div>
                  </CDropdownItem>
                  <CDropdownItem className=" no-hover bg-secondary facility-map-theme__filter-item">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                      <CFormLabel style={{ flex: 1, marginRight: '8px' }}>Provider</CFormLabel>
                      <Select options={providerOptions} onChange={handleProviderChange} value={providerOptions.find((option) => option.value === selectedProviderID)} className="w-100" classNamePrefix="facility-map-select" closeMenuOnSelect={false} isSearchable={true} />
                    </div>
                  </CDropdownItem>
                  <CDropdownItem className=" no-hover bg-secondary facility-map-theme__filter-item">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                      <CFormLabel style={{ flex: 1, marginRight: '8px' }}>All Practice Facilities</CFormLabel>
                      <div className="form-check form-switch  ms-2">
                        <input className={`form-check-input ${!isToggled ? 'bg-success border-success' : ''}`} type="checkbox" role="switch" id="toggleSwitch" checked={!isToggled} onChange={handleToggle} disabled={selectedProvider.length > 0 ? false : true} />
                        <label className="form-check-label" htmlFor="toggleSwitch">
                          {isToggled ? 'Hide' : 'Show'}
                        </label>
                      </div>
                    </div>
                  </CDropdownItem>
                  <CDropdownItem className="no-hover bg-secondary facility-map-theme__filter-item">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                      <CFormLabel style={{ flex: 1, marginRight: '8px' }}>County</CFormLabel>
                      <Select options={countryOption} onChange={handleCountyChange} value={countryOption.find((option) => option.value === selectedCounty)} className="w-100" classNamePrefix="facility-map-select" closeMenuOnSelect={false} isSearchable={true} />
                    </div>
                  </CDropdownItem>
                  <CDropdownItem href="#" className="no-hover bg-secondary facility-map-theme__filter-item">
                    {' '}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                      <CFormLabel style={{ flex: 1, marginRight: '8px' }}>Facility Type</CFormLabel>
                      <Select options={FacilityOption} onChange={handleFacilityChange} value={selectedFacility} className="w-100" classNamePrefix="facility-map-select" closeMenuOnSelect={false} isSearchable={true} />
                    </div>
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            )}
          </CCol>
        </CRow>

        <div>
          <FacilityMap
            selectedProviderFacilities={selectedProviderFacilities}
            Radius={radius}
            onFilter={onFilter}
            providerList={selectedProvider}
            facilities={mapFacilities}
            types={facilityType}
            MarkerClick={markerClick}
            setFacilities={setFacilities}
            isDropdownVisible={isDropdownVisible}
            scheduleAppointmentPatinet={scheduleAppointmentPatinet}
            controlledByParent={dashboardMapView}
            selectedDiagnosisName={diagnosisOptions.find((opt) => opt.label === dashboardFilters.diagnosis || opt.value === dashboardFilters.diagnosis)?.label || ''}
            selectedStatus={dashboardFilters.countStatus}
            selectedFacilityType={dashboardFilters.facilityType === 'all' ? null : dashboardFilters.facilityType}
          ></FacilityMap>
        </div>

        <CModal size="md" contentClassName="dashboard-dark-theme" visible={modalVisible} backdrop={'static'} keyboard={false} onClose={() => setModalVisible(false)}>
          <CModalHeader onClose={() => setModalVisible(false)}>
            <CModalTitle>Add Facility</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <FacilityForm Lat={rowData.current.Lat} Lng={rowData.current.Lng} initialAddress={rowData.current.Address} Mode={'INSERT'} typeOptions={options} initialName={rowData.current.Name} Status={'Pending'} onSubmit={submitFn}></FacilityForm>
          </CModalBody>
        </CModal>

        <CModal size="lg" className="facility-details-modal" contentClassName="dashboard-dark-theme" backdrop={'static'} keyboard={false} visible={visible} onClose={() => setVisible(false)}>
          <CModalHeader onClose={() => setVisible(false)}>
            <CModalTitle>Facility Details</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <CNav className="justify-content-center" variant="pills">
              <CNavItem>
                <CNavLink active={activeTab === 'changeFacilityDetails'} onClick={() => setActiveTab('changeFacilityDetails')}>
                  Credentialing
                </CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink active={activeTab === 'FacilityDetails'} onClick={() => setActiveTab('FacilityDetails')}>
                  Facility Details
                </CNavLink>
              </CNavItem>
            </CNav>
            <CTabContent>
              <CTabPane visible={activeTab === 'changeFacilityDetails'}>
                <CForm>
                  <CRow className="mb-3 mt-3">
                    <CCol xs="6">
                      <CFormLabel>Facility</CFormLabel>
                      <CFormInput value={selectedEditFacility} readOnly />
                    </CCol>

                    <CCol xs="6">
                      {(() => {
                        const isFacilityInSelectedProvider = selectedProviderFacilities.some((selectedFacility) => parseInt(selectedFacility.FacilityID) == updateFacility.ID)

                        return (
                          <>
                            {/*isFacilityInSelectedProvider ? <CFormLabel>Has the provider been credentialed for this facility?</CFormLabel> : <CFormLabel>Do you want to add the facility for credentialing?</CFormLabel>*/}

                            <CFormLabel>Credentialed ?</CFormLabel>
                            {/* <Select options={credentialOption} value={credentialOption.find((opt) => opt.value === selectedCredentialed)} onChange={(opt) => setSelectedCredentialed(opt.value)} /> */}
                            <Select
                              className="w-100"
                              classNamePrefix="facility-map-select"
                              options={credentialOption}
                              value={credentialOption.find(
                                (opt) => opt.value === selectedCredentialed
                              )}
                              onChange={(opt) => setSelectedCredentialed(opt.value)}
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                          </>
                        )
                      })()}
                    </CCol>
                  </CRow>
                </CForm>
                <CModalFooter>
                  <CButton color="primary" onClick={handleModalSubmit}>
                    {'Save'}
                  </CButton>
                </CModalFooter>
              </CTabPane>
              <CTabPane visible={activeTab === 'FacilityDetails'}>
                <Form form={schema} submission={formAnswer} />
                <CModalFooter>
                  <CButton color="secondary" onClick={() => setVisible(false)}>
                    Close
                  </CButton>
                </CModalFooter>
              </CTabPane>
            </CTabContent>
          </CModalBody>
        </CModal>

        <CModal size="md" className="mb-5 mt-5" contentClassName="dashboard-dark-theme" margin-top='20px' backdrop={'static'} keyboard={false} visible={showCredentialingModal} onClose={() => setShowCredentialingModal(false)}>
          <CModalHeader onClose={() => setShowCredentialingModal(false)}>
            <CModalTitle>Start Credentialing</CModalTitle>
          </CModalHeader>
          <CModalBody className="text-center py-4">
            <CForm>
              <CRow className="mb-2 mt-2">
                <h6 style={{ color: '#333', alignContent: 'center' }}>Are you sure you want to start credentialing for <strong>{selectedEditFacility}</strong>?</h6>
              </CRow>
            </CForm>
          </CModalBody>
          <CModalFooter className="justify-content-center gap-1 py-2">
            <CButton color="secondary" variant="outline" onClick={handleCredentialingCancel}>
              close
            </CButton>
            <CButton color="primary" onClick={handleCredentialingConfirm}>
              Start credentialing
            </CButton>
          </CModalFooter>
        </CModal>
      </div>
    </>
  )
}

export default AddFacility
